import { Box, type SxProps, type Theme, type TooltipProps } from "@mui/material";
import type { FC, ReactElement, ReactNode } from "react";
import { HOVER_CARD, MOTION, NEUTRAL, SHADOW } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

type Props = {
	/**
	 * What the card shows. Mounted only while the card is open, so a component that fetches can sit here
	 * and fetch on first hover rather than when the trigger renders.
	 */
	content: ReactNode;
	/**
	 * The trigger: one element that holds a ref and takes event props (an `<a>`, a MUI `Link`, a focusable
	 * `<span>`). It opens the card on hover and on keyboard focus; Escape closes it.
	 */
	children: ReactElement;
	/** DEFAULT: `bottom-start` — the card hangs below the name, starting at its left edge, like a menu. */
	placement?: TooltipProps["placement"];
	/** Told when the card opens and closes — for a trigger that tints while its card is showing. Absent = not told. */
	onOpenChange?: (open: boolean) => void;
	dataTestId?: string;
};

/**
 * The card's surface: a floating panel rather than a tooltip's dark chip — the content is read, often a
 * table, and can be wider than the trigger by a lot, so it scrolls within `HOVER_CARD` bounds.
 */
const CARD_SX: SxProps<Theme> = (theme) => ({
	p: 0,
	maxWidth: HOVER_CARD.MAX_WIDTH,
	minWidth: HOVER_CARD.MIN_WIDTH,
	maxHeight: HOVER_CARD.MAX_HEIGHT,
	overflow: "auto",
	backgroundColor: theme.palette.background.paper,
	color: theme.palette.text.primary,
	border: `1px solid ${NEUTRAL[200]}`,
	borderRadius: HOVER_CARD.RADIUS,
	boxShadow: SHADOW.FLOATING_WINDOW,
	fontSize: "inherit",
	fontWeight: 400,
});

/**
 * A read-only preview card that opens over a name on hover or keyboard focus — the facts behind a label
 * without leaving the page (the settings rows behind a wage breakdown line, a record's summary behind its
 * name). Built on `InHouseTooltip`, so it opens on hover and focus-visible and closes on Escape; the card
 * DESCRIBES its trigger (`describeChild`), so a link keeps its own accessible name. The pointer can cross
 * onto the card to scroll it. For a short plain-text hint use `InHouseTooltip` / `InfoIconWithTooltip`.
 *
 * Why not `SystemReactTinyPopover`'s hover mode, which also has enter/leave delays and Escape: it wraps the
 * trigger in a `<div>` to attach its listeners (hover mode cannot drop the wrapper), and a block inside a
 * line of text — the names sit inside `<p>`/`<span>` ledger labels — is invalid markup that breaks the
 * line; its card is not tied to the trigger for assistive tech, where this one describes it; and it opens
 * on any focus, so a mouse click on a link would flash the card, where this one waits for focus-visible.
 */
export const HoverCard: FC<Props> = ({ content, children, placement = "bottom-start", onOpenChange, dataTestId }) => (
	<InHouseTooltip
		title={<Box data-testid={dataTestId}>{content}</Box>}
		describeChild
		placement={placement}
		enterDelay={MOTION.DELAY.HOVER_CARD_ENTER}
		enterNextDelay={MOTION.DELAY.HOVER_CARD_ENTER}
		leaveDelay={MOTION.DELAY.HOVER_CARD_LEAVE}
		slotProps={{ tooltip: { sx: CARD_SX } }}
		onOpen={() => onOpenChange?.(true)}
		onClose={() => onOpenChange?.(false)}
	>
		{children}
	</InHouseTooltip>
);
